import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const KEY = "footsie_terms_ack";

export default function ConsentGate() {
    const { user, refresh } = useAuth();
    const [needsTerms, setNeedsTerms] = useState(false);
    const [needsAge, setNeedsAge] = useState(false);
    const [currentVersion, setCurrentVersion] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/legal/version").then((r) => {
            const cur = r.data.terms_version;
            setCurrentVersion(cur);
            if (cur && user.accepted_terms_version && user.accepted_terms_version !== cur) {
                setNeedsTerms(true);
            }
        }).catch(() => {});
        // Optional age verification prompt — one-time per session
        try {
            if (!user.age_verified && !sessionStorage.getItem(KEY)) {
                setNeedsAge(true);
            }
        } catch (_) { /* ignore */ }
    }, [user]);

    if (!user) return null;

    const acceptTerms = async () => {
        setBusy(true);
        try {
            await api.post("/legal/accept", { version: currentVersion });
            toast.success("Thanks — you're up to date");
            setNeedsTerms(false);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const verifyAgeMock = async () => {
        setBusy(true);
        try {
            const start = await api.post("/verify/age/start");
            await api.post("/verify/age/complete", {
                method: "mocked",
                provider_reference: start.data.provider_reference,
            });
            toast.success("Age confirmed (MOCKED)");
            try { sessionStorage.setItem(KEY, "1"); } catch (_) { /* ignore */ }
            setNeedsAge(false);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const dismissAge = () => {
        try { sessionStorage.setItem(KEY, "1"); } catch (_) { /* ignore */ }
        setNeedsAge(false);
    };

    if (needsTerms) {
        return (
            <div className="fixed inset-0 bg-[#0A0A0A]/50 z-[60] flex items-center justify-center p-4" data-testid="terms-consent-gate">
                <div className="bg-white brut-lg rounded-xl p-6 max-w-md w-full">
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck className="w-6 h-6" />
                        <h2 className="display text-2xl font-black">Terms updated</h2>
                    </div>
                    <p className="text-sm text-[#4A4A4A] font-medium mb-4">
                        We&apos;ve updated our Terms of Service and Privacy Policy since you last agreed. Please review and re-accept to keep using footsie.
                    </p>
                    <div className="flex gap-2">
                        <Link to="/terms" target="_blank" className="px-4 py-2 rounded-lg brut-inset bg-white font-bold text-sm">Read Terms</Link>
                        <Link to="/privacy" target="_blank" className="px-4 py-2 rounded-lg brut-inset bg-white font-bold text-sm">Read Privacy</Link>
                        <button
                            onClick={acceptTerms}
                            disabled={busy}
                            className="ml-auto px-4 py-2 rounded-lg brut bg-[#0A0A0A] text-white font-bold text-sm"
                            data-testid="terms-accept"
                        >
                            Accept & continue
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (needsAge) {
        return (
            <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-40" data-testid="age-verify-prompt">
                <div className="brut-lg bg-white rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#FFB8D1] border-2 border-[#0A0A0A] flex items-center justify-center shrink-0">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-black text-sm">Verify you&apos;re 18+</div>
                            <div className="text-xs text-[#4A4A4A] font-medium mt-0.5">
                                Get a verified badge and unlock features that require legal age of majority.
                                <span className="ml-1 px-1 rounded bg-[#FFE55C] text-[10px] font-black uppercase">MOCKED</span>
                            </div>
                            <div className="mt-2 flex gap-2">
                                <button
                                    onClick={verifyAgeMock}
                                    disabled={busy}
                                    className="px-3 py-1.5 rounded-lg brut bg-[#0A0A0A] text-white font-bold text-xs"
                                    data-testid="age-verify-start"
                                >
                                    Verify (demo)
                                </button>
                                <button
                                    onClick={dismissAge}
                                    className="px-3 py-1.5 rounded-lg brut-inset bg-white font-bold text-xs"
                                    data-testid="age-verify-later"
                                >
                                    Later
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
